import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line } from "recharts";
import { motion } from "framer-motion";

export default function Statements() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => { const r = await api.get("/statements/pnl"); setData(r.data); })();
  }, []);

  if (!data) return <div className="text-white/50">Loading…</div>;

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-2">Statements</div>
        <h1 className="font-display text-4xl">Profit & loss · cashflow</h1>
        <p className="text-white/60 text-sm mt-2">Live view generated from your receipts and bank feed.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <Stat label="Total income" value={data.total_income} color="#00A86B"/>
        <Stat label="Bank outflow" value={data.total_expense_bank} color="#E63946"/>
        <Stat label="Receipt expenses" value={data.total_receipt_expense} color="#FFB020"/>
        <Stat label="Net" value={data.net} color={data.net >= 0 ? "#00A86B" : "#E63946"}/>
      </div>

      <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} className="glass rounded-xl p-6">
        <div className="eyebrow mb-4">Monthly cashflow</div>
        <div className="h-72">
          {data.monthly.length === 0 ? <div className="text-white/50 text-sm text-center pt-16">Import bank transactions to see cashflow.</div> :
          <ResponsiveContainer>
            <LineChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: "#9BA3A0", fontSize: 11 }} />
              <YAxis tick={{ fill: "#9BA3A0", fontSize: 11 }} />
              <Tooltip contentStyle={{ background:"#0A1311", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10 }}/>
              <Legend wrapperStyle={{ color: "#9BA3A0", fontSize: 12 }} />
              <Line dataKey="income" stroke="#00A86B" strokeWidth={2} dot={false} />
              <Line dataKey="expense" stroke="#E63946" strokeWidth={2} dot={false} />
              <Line dataKey="net" stroke="#FFB020" strokeWidth={2} dot={{ r: 3, fill: "#FFB020" }} />
            </LineChart>
          </ResponsiveContainer>}
        </div>
      </motion.div>

      <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:0.1}} className="glass rounded-xl p-6">
        <div className="eyebrow mb-4">Expense by category (from receipts)</div>
        <div className="h-72">
          {data.by_category.length === 0 ? <div className="text-white/50 text-sm text-center pt-16">No receipts captured yet.</div> :
          <ResponsiveContainer>
            <BarChart data={data.by_category}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="category" tick={{ fill: "#9BA3A0", fontSize: 11 }} />
              <YAxis tick={{ fill: "#9BA3A0", fontSize: 11 }} />
              <Tooltip contentStyle={{ background:"#0A1311", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10 }}/>
              <Bar dataKey="amount" fill="#FFB020" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>}
        </div>
      </motion.div>
    </div>
  );
}

const Stat = ({ label, value, color }) => (
  <div className="glass rounded-xl p-5">
    <div className="eyebrow">{label}</div>
    <div className="mono text-2xl mt-3" style={{ color }}>€{Number(value||0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
  </div>
);
